import React from "react";
import {
  List,
  Button,
  Row,
  Col,
  Modal,
  Input,
  notification,
  Divider,
  FloatButton,
} from "antd";
import { gql } from "@apollo/client";
import { client } from "../App";
import prettyDateString from "../lib/prettyDateString";
import { statusIcon, agentStatus } from "../lib/status";
import InfiniteList from "./InfiniteList";
import { DEFAULT_FEED_QUERY_LIMIT } from "../lib/pagination";
import { migrationRelayUrl } from "../lib/env";

export const AGENT_FEED_QUERY = gql`
  query agentFeed($offset: Int, $limit: Int, $queryString: String) {
    agentFeed(offset: $offset, limit: $limit, queryString: $queryString) {
      agents {
        id
        createdAt
        updatedAt
        token
        validationCode
        lastCheckInAt
        name
        version
      }
      totalCount
      offset
      limit
      queryString
    }
  }
`;

const CREATE_AGENT_MUTATION = gql`
  mutation createAgent($name: String) {
    createAgent(name: $name) {
      id
    }
  }
`;

export const GENERATE_AGENT_VALIDATION_CODE_MUTATION = gql`
  mutation generateAgentValidationCode($id: ID!) {
    generateAgentValidationCode(id: $id) {
      id
    }
  }
`;

export const REMOVE_AGENT_VALIDATION_CODE_MUTATION = gql`
  mutation removeAgentValidationCode($id: ID!) {
    removeAgentValidationCode(id: $id) {
      id
    }
  }
`;

export const DELETE_AGENT_MUTATION = gql`
  mutation deleteAgent($id: ID!) {
    deleteAgent(id: $id) {
      id
    }
  }
`;

export async function deleteAgent(agentId: string | number) {
  client
    .mutate({
      mutation: DELETE_AGENT_MUTATION,
      variables: { id: agentId },
    })
    .then(() => {
      notification["success"]({
        message: "Successfully deleted Agent",
      });
    })
    .catch((err) => {
      notification["warning"]({
        message: "Failed to delete Agent",
        description: err.message,
      });
    });
}

export async function generateValidationCode(agentId: string | number) {
  client
    .mutate({
      mutation: GENERATE_AGENT_VALIDATION_CODE_MUTATION,
      variables: { id: agentId },
    })
    .then(() => {
      notification["success"]({
        message: "Generated new validation code",
      });
    })
    .catch((err) => {
      notification["warning"]({
        message: "Failed to generate new validation code",
        description: err.message,
      });
    });
}

export async function removeValidationCode(agentId: string | number) {
  client
    .mutate({
      mutation: REMOVE_AGENT_VALIDATION_CODE_MUTATION,
      variables: { id: agentId },
      refetchQueries: [{ query: AGENT_FEED_QUERY }],
    })
    .then(() => {
      notification["success"]({
        message: "Successfully removed validation code",
      });
    })
    .catch((err) => {
      notification["warning"]({
        message: "Failed to remove validation code",
        description: err.message,
      });
    });
}

export async function refreshRelayConnection(agentToken: string) {
  const refreshConnectionUrl = new URL(
    "/api/clear_connection",
    migrationRelayUrl,
  );
  fetch(refreshConnectionUrl.toString(), {
    method: "post",
    body: JSON.stringify({ agentToken: agentToken }),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (response) => {
      if (!response.ok) {
        const errorString = await response.json();
        throw errorString;
      }
    })
    .then(() => {
      notification["success"]({
        message: "Successfully refreshed relay connection",
      });
    })
    .catch((err) => {
      notification["warning"]({
        message: "Failed to refresh relay connection",
        description: err.error,
      });
    });
}

export function agentTitle(agent): JSX.Element {
  if (agent.name) {
    return (
      <a href={`/agents/${agent.id}`}>
        Agent # {agent.id} - {agent.name}
      </a>
    );
  } else {
    return <a href={`/agents/${agent.id}`}>Agent # {agent.id}</a>;
  }
}

export function lastCheckIn(agent): string {
  let checkInString: string;
  if (agent.lastCheckInAt == null) {
    checkInString = "Never";
  } else {
    if (agent.version) {
      checkInString = `${prettyDateString(agent.lastCheckInAt)}  -  Version: ${
        agent.version
      }`;
    } else {
      checkInString = prettyDateString(agent.lastCheckInAt);
    }
  }
  return checkInString;
}

export function validationCode(agent): string {
  if (agent.validationCode == null) {
    if (agent.lastCheckInAt) {
      return "Code redeemed";
    } else {
      return "Code removed";
    }
  } else {
    return agent.validationCode;
  }
}

const AgentList = () => {
  return (
    <div>
      <FloatButton.BackTop />
      <h1 style={{ float: "left" }}>Agents</h1>
      <div style={{ float: "right" }}>
        <CreateAgentModal />
      </div>
      <Divider />
      <InfiniteList
        query={AGENT_FEED_QUERY}
        recordKey="agents"
        perPage={DEFAULT_FEED_QUERY_LIMIT}
        showSearch={true}
        pollInterval={1000}
        listProps={{
          itemLayout: "vertical",
        }}
        renderListItem={(agent: any) => (
          <List.Item
            key={agent.id}
            actions={[
              <a onClick={() => generateValidationCode(agent.id)}>
                Generate Validation Code
              </a>,
              <a onClick={() => removeValidationCode(agent.id)}>
                Remove Validation Code
              </a>,
              <a onClick={() => refreshRelayConnection(agent.token)}>
                Refresh Relay Connection
              </a>,
            ]}
          >
            <List.Item.Meta
              avatar={statusIcon(agentStatus(agent), 60, "avatar")}
              title={agentTitle(agent)}
              description={`Last Check In ${lastCheckIn(agent)}`}
            />
            <Row gutter={48}>
              <Col span={10}>
                <b>Agent Token: </b> {agent.token}
              </Col>
              <Col span={10}>
                <b>Created At: </b>
                {prettyDateString(agent.createdAt)}
              </Col>
            </Row>
            <Row gutter={48}>
              <Col span={10}>
                <b>Validation Code: </b> {validationCode(agent)}
              </Col>
              <Col span={10}>
                <b>Updated At: </b>
                {prettyDateString(agent.updatedAt)}
              </Col>
            </Row>
          </List.Item>
        )}
      />
    </div>
  );
};

const defaultAgentState = {
  agentName: "",
  visible: false,
  confirmLoading: false,
};

export class CreateAgentModal extends React.Component {
  state = defaultAgentState;

  showModal = () => {
    this.setState({
      visible: true,
    });
  };

  handleOk = () => {
    this.setState({
      confirmLoading: true,
    });

    const agentName = this.state.agentName;

    setTimeout(() => {
      client
        .mutate({
          mutation: CREATE_AGENT_MUTATION,
          variables: { name: agentName },
          refetchQueries: [{ query: AGENT_FEED_QUERY }],
        })
        .then(() => {
          notification["success"]({
            message: "Successfully created new Agent",
          });
        })
        .catch((err) => {
          notification["error"]({
            message: "Failed to create Agent",
            description: err.message,
          });
        });
      this.setState(defaultAgentState);
    }, 2000);
  };

  handleCancel = () => {
    this.setState(defaultAgentState);
  };

  handleNameChange = (e) => {
    this.setState({
      agentName: e.target.value,
    });
  };

  render() {
    const { visible, confirmLoading, agentName } = this.state;
    return (
      <div>
        <Button type="primary" onClick={this.showModal}>
          Create New Agent
        </Button>

        <Modal
          title="Create New Agent"
          visible={visible}
          onOk={this.handleOk}
          confirmLoading={confirmLoading}
          onCancel={this.handleCancel}
        >
          <Input
            placeholder="Agent Name"
            value={agentName}
            onChange={this.handleNameChange}
          />
        </Modal>
      </div>
    );
  }
}

export default AgentList;
